import { Flex, IconButton, Text, Tooltip } from '@lego/klik-ui';
import { CheckTickBold, Clone } from '@lego/klik-ui/icons';
import type { MouseEvent, ReactNode } from 'react';
import { useCopyToClipboard } from '@/hooks/common/useCopyToClipboard';

type TruncationMode = 'prefixSuffix' | 'prefixOnly';

type Props = {
  value: string;
  prefixLength?: number;
  suffixLength?: number;
  truncationMode?: TruncationMode;
  copyLabel?: string;
  textProps?: Record<string, unknown>;
  trailingElement?: ReactNode;
};

export const CopyableText = ({
  value,
  prefixLength,
  suffixLength,
  truncationMode = 'prefixSuffix',
  copyLabel = 'Copy full value',
  textProps,
  trailingElement,
}: Props) => {
  const { copied, error, copy } = useCopyToClipboard();

  const getDisplayValue = () => {
    if (truncationMode === 'prefixOnly' && prefixLength !== undefined) {
      if (value.length > prefixLength) {
        return `${value.slice(0, prefixLength)}…`;
      }
      return value;
    }

    if (prefixLength === undefined) return value;

    const suffix = suffixLength ?? 0;
    const minLength = prefixLength + suffix + 3;
    if (value.length > minLength) {
      const end = suffix > 0 ? value.slice(-suffix) : '';
      return `${value.slice(0, prefixLength)}...${end}`;
    }
    return value;
  };

  const displayValue = getDisplayValue();

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    await copy(value);
  };

  const getTooltipLabel = () => {
    if (copied) return 'Copied!';
    if (error) return 'Copy failed';
    return copyLabel;
  };

  return (
    <Flex align="center" flex={1} gap={2} overflow="hidden" width="100%">
      <Tooltip label={value}>
        <Text
          flex="0 1 auto"
          fontSize="md"
          minWidth={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          {...textProps}
        >
          {displayValue}
        </Text>
      </Tooltip>
      <Tooltip
        isOpen={copied || error ? true : undefined}
        key={copied ? 'copied' : error ? 'error' : 'copy'}
        label={getTooltipLabel()}
      >
        <IconButton
          aria-label={copyLabel}
          icon={copied ? <CheckTickBold color="green" /> : <Clone />}
          onClick={handleCopy}
          size="xs"
          variant="ghost"
        />
      </Tooltip>
      {trailingElement}
    </Flex>
  );
};
